import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { subscriptionAPI } from '../../lib/api';

export const TrialExpiredModal = ({ onSubscribe }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await subscriptionAPI.checkTrialAccess();
      setAccessStatus(response.data);
      
      // Show modal if access is blocked
      if (!response.data.has_access && response.data.status === 'trial_expired') {
        setOpen(true);
      }
    } catch (error) {
      console.error('Failed to check trial access:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      // Redirect to subscription page
      navigate('/settings?tab=subscription');
      setOpen(false);
    } catch (error) {
      console.error('Failed to start upgrade:', error);
    } finally {
      setUpgrading(false);
    }
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@leappro.ca?subject=Trial Extension Request';
  };

  if (loading) return null;
  if (!accessStatus || accessStatus.has_access) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <DialogTitle className="text-xl">Your Trial Has Expired</DialogTitle>
          <DialogDescription className="text-base mt-2">
            Your 14-day free trial has ended. To continue using LEAP Pro and access all your data, please add your payment details.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Benefits reminder */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <p className="font-medium text-slate-700 text-sm">Continue enjoying:</p>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Unlimited case management
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Client portal access
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Document generation & storage
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                All your existing data preserved
              </li>
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2">
            <Button 
              onClick={handleUpgrade}
              disabled={upgrading}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="upgrade-now-btn"
            >
              {upgrading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Add Payment Details
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleContactSupport}
              className="w-full"
              data-testid="contact-support-btn"
            >
              Contact Support
            </Button>
          </div>

          <p className="text-xs text-center text-slate-500">
            Your data is safe and will be available once you subscribe.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrialExpiredModal;
